import { Element, useEditor, useNode } from "@craftjs/core";
import * as React from "react";
import { Actions } from "src/types";
import { ThemeContext } from "styled-components";

import TopLevelFloatingOptions from "@/components/editor/block-outline/floating-options/top-level-floating-component";
import type { ImagePosition } from "@/components/editor/image-positioner/image-positioner-component";
import Button from "@/components/editor/primitives/button/button-component";
import RichText from "@/components/editor/primitives/rich-text";
import Text from "@/components/editor/primitives/text/text-component";
import BackgroundSquiggle from "@/components/editor/shared/background-squiggle";
import {
  BlockBackgroundColorWrapper,
  BlockMaxWidthWrapper,
} from "@/components/editor/shared/block-wrapper";
import { blackLogo, whiteLogo } from "@/components/editor/shared/header/assets";
import formatLogoSize from "@/components/editor/shared/header/format-logo-size";
import HeaderSettings, {
  ImageFit,
} from "@/components/editor/shared/header/header-settings-component";
import {
  ColorLabels,
  useInvertedColor,
} from "@/components/editor/utils/use-color";
import useRichTextCompatible from "@/hooks/use-rich-text-compatible";

import { DEFAULT_BUTTON_TEXT, DEFAULT_SUBHEADING_TEXT } from "./constants";
import {
  ButtonWrapper,
  Container,
  Content,
  Image,
  ImageWrapper,
  Logo,
  LogoWrapper,
  PaddingWrapper,
  Wrapper,
} from "./header-option-2-component-styles";

type HeaderOption2Props = {
  backgroundImage?: string;
  backgroundImageAlt?: string;
  backgroundPosition?: ImagePosition;
  ctaText: string;
  heading: string;
  isLogoShowing?: boolean;
  logo?: string;
  logoSize?: number;
  imageFit?: ImageFit;
};

const CustomHeaderSettings = () => {
  return (
    <HeaderSettings
      optimalImageSize="600x648px"
      backgroundImageSize={{
        width: "240",
        height: "300",
      }}
    />
  );
};

export default function HeaderOption2(props: HeaderOption2Props) {
  const {
    connectors: { connect },
    id: nodeId,
  } = useNode();

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const theme = React.useContext(ThemeContext);

  const showLightLogo =
    useInvertedColor(theme.colors[ColorLabels.BACKGROUND]) === "#ffffff";

  const defaultLogoImage = showLightLogo ? whiteLogo : blackLogo;

  const isRichTextCompatible = useRichTextCompatible(nodeId, [
    "heading",
    "cta-text",
    "subheading",
  ]);

  return (
    <BlockBackgroundColorWrapper
      ref={connect}
      id={nodeId}
      className={!enabled ? "disable-scroll" : undefined}
    >
      <PaddingWrapper>
        <BlockMaxWidthWrapper>
          <Wrapper>
            <BackgroundSquiggle />
            <Container>
              <Content>
                {props.isLogoShowing && (
                  <LogoWrapper>
                    <Logo
                      draggable="false"
                      src={props.logo || defaultLogoImage}
                      alt="Header Logo"
                      data-testid="header-option-2-logo"
                      style={{
                        width: formatLogoSize(props.logoSize),
                        height: "auto",
                      }}
                      isLogoResized={!isNaN(props.logoSize)}
                    />
                  </LogoWrapper>
                )}
                {isRichTextCompatible ? (
                  <>
                    <Element
                      id="heading"
                      is={RichText}
                      text={`<h2>${props.heading}</h2>`}
                    />
                    <Element
                      id="subheading"
                      is={RichText}
                      text={`<p>${DEFAULT_SUBHEADING_TEXT}</p>`}
                    />
                    <Element
                      id="cta-text"
                      is={RichText}
                      text={`<h5>${props.ctaText}</h5>`}
                    />
                  </>
                ) : (
                  <>
                    <Element
                      id="heading"
                      is={Text}
                      element="h1"
                      text={props.heading}
                      pattern="headingOne"
                    />
                    <Element
                      id="subheading"
                      is={Text}
                      text={DEFAULT_SUBHEADING_TEXT}
                      pattern="paragraph"
                    />
                    <Element
                      id="cta-text"
                      is={Text}
                      text={props.ctaText}
                      pattern="headingFour"
                    />
                  </>
                )}
                <ButtonWrapper>
                  <Element
                    id="cta-button"
                    is={Button}
                    text={DEFAULT_BUTTON_TEXT}
                    action={{
                      kind: Actions.TicketSelector,
                    }}
                  />
                </ButtonWrapper>
              </Content>
              <ImageWrapper>
                <Image
                  imageFit={props.imageFit}
                  $position={props.backgroundPosition || "center center"}
                  src={props.backgroundImage}
                  alt={props.backgroundImageAlt || ""}
                />
              </ImageWrapper>
            </Container>
          </Wrapper>
        </BlockMaxWidthWrapper>
      </PaddingWrapper>
    </BlockBackgroundColorWrapper>
  );
}

HeaderOption2.craft = {
  custom: {
    displayName: "Header option #2",
  },
  related: {
    settings: CustomHeaderSettings,
    leftOutlineOptions: TopLevelFloatingOptions,
  },
};
